import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgPolicy, PolicyAcknowledgment } from '@newgenus/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../material.module';
import {
    PolicyAcknowledgmentDialogComponent,
    PolicyAcknowledgmentDialogData,
} from '../org-policy-acknowledgment-dialog/org-policy-acknowledgment-dialog';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { Subscription, take } from 'rxjs';
import firebase from 'firebase/compat/app';

export interface MyPoliciesDialogData {
    organization: Organization;
}

interface PolicyCard {
    acknowledgment: PolicyAcknowledgment;
    policy?: OrgPolicy;
    isExpanded: boolean;
}

@Component({
    selector: 'shared-org-my-policies-dialog',
    imports: [MaterialModule, CommonModule, FormsModule],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2">fact_check</mat-icon>
            My Policies
            <span class="badge pending-badge" *ngIf="pendingPolicies.length > 0">
                {{ pendingPolicies.length }} pending
            </span>

            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content">
            <!-- Loading State -->
            <div *ngIf="isLoading" class="loading-state">
                <mat-spinner diameter="40"></mat-spinner>
                <p>Loading your policies...</p>
            </div>

            <!-- Tab Group -->
            <mat-tab-group *ngIf="!isLoading" animationDuration="200ms">
                <!-- Pending Tab -->
                <mat-tab>
                    <ng-template mat-tab-label>
                        <mat-icon class="tab-icon">pending_actions</mat-icon>
                        Pending
                        <span class="tab-badge" *ngIf="pendingPolicies.length > 0">{{ pendingPolicies.length }}</span>
                    </ng-template>

                    <div class="tab-content">
                        <div *ngIf="pendingPolicies.length === 0" class="empty-state">
                            <mat-icon class="empty-icon">check_circle</mat-icon>
                            <h3>All caught up!</h3>
                            <p>You have no pending policies to acknowledge.</p>
                        </div>

                        <div class="policies-list" *ngIf="pendingPolicies.length > 0">
                            <mat-card *ngFor="let item of pendingPolicies" class="policy-card pending">
                                <mat-card-header>
                                    <div mat-card-avatar class="policy-avatar pending-avatar">
                                        <mat-icon>policy</mat-icon>
                                    </div>
                                    <mat-card-title>{{ item.acknowledgment.policyName }}</mat-card-title>
                                    <mat-card-subtitle>
                                        <span *ngIf="item.acknowledgment.requiresReacknowledgment" class="reack-badge">
                                            <mat-icon class="inline-icon">refresh</mat-icon>
                                            Re-acknowledgment Required (v{{ item.acknowledgment.newPolicyVersion }})
                                        </span>
                                        <span *ngIf="!item.acknowledgment.requiresReacknowledgment">
                                            Version {{ item.acknowledgment.policyVersion }}
                                        </span>
                                    </mat-card-subtitle>
                                </mat-card-header>

                                <mat-card-content>
                                    <!-- Acknowledgment Progress -->
                                    <div class="acknowledgment-progress">
                                        <div class="step" [class.completed]="item.acknowledgment.hasRead">
                                            <mat-icon>{{
                                                item.acknowledgment.hasRead ? 'check_circle' : 'radio_button_unchecked'
                                            }}</mat-icon>
                                            <span>Read</span>
                                        </div>
                                        <div class="step" [class.completed]="item.acknowledgment.hasUnderstood">
                                            <mat-icon>{{
                                                item.acknowledgment.hasUnderstood
                                                    ? 'check_circle'
                                                    : 'radio_button_unchecked'
                                            }}</mat-icon>
                                            <span>Understood</span>
                                        </div>
                                        <div class="step" [class.completed]="item.acknowledgment.hasAgreed">
                                            <mat-icon>{{
                                                item.acknowledgment.hasAgreed
                                                    ? 'check_circle'
                                                    : 'radio_button_unchecked'
                                            }}</mat-icon>
                                            <span>Agreed</span>
                                        </div>
                                    </div>

                                    <!-- Expandable Policy Content -->
                                    <div
                                        *ngIf="item.isExpanded"
                                        class="policy-content"
                                        [innerHTML]="item.policy?.policyWriting || 'Loading...'"
                                    ></div>
                                </mat-card-content>

                                <mat-card-actions align="end">
                                    <button mat-button (click)="toggleExpand(item)" color="primary">
                                        <mat-icon>{{ item.isExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                                        {{ item.isExpanded ? 'Hide' : 'View' }} Policy
                                    </button>
                                    <button mat-raised-button color="primary" (click)="openAcknowledgmentDialog(item)">
                                        <mat-icon>fact_check</mat-icon>
                                        Acknowledge
                                    </button>
                                </mat-card-actions>
                            </mat-card>
                        </div>
                    </div>
                </mat-tab>

                <!-- Acknowledged Tab -->
                <mat-tab>
                    <ng-template mat-tab-label>
                        <mat-icon class="tab-icon">verified</mat-icon>
                        Acknowledged
                        <span class="tab-badge success" *ngIf="acknowledgedPolicies.length > 0">{{
                            acknowledgedPolicies.length
                        }}</span>
                    </ng-template>

                    <div class="tab-content">
                        <div *ngIf="acknowledgedPolicies.length === 0" class="empty-state">
                            <mat-icon class="empty-icon">history</mat-icon>
                            <h3>No acknowledged policies yet</h3>
                            <p>Policies you acknowledge will appear here.</p>
                        </div>

                        <div class="policies-list" *ngIf="acknowledgedPolicies.length > 0">
                            <mat-card *ngFor="let item of acknowledgedPolicies" class="policy-card acknowledged">
                                <mat-card-header>
                                    <div mat-card-avatar class="policy-avatar acknowledged-avatar">
                                        <mat-icon>verified</mat-icon>
                                    </div>
                                    <mat-card-title>{{ item.acknowledgment.policyName }}</mat-card-title>
                                    <mat-card-subtitle>
                                        Version {{ item.acknowledgment.policyVersion }} · Acknowledged
                                        {{ formatDate(item.acknowledgment.completedAt) }}
                                    </mat-card-subtitle>
                                </mat-card-header>

                                <mat-card-content
                                    *ngIf="
                                        item.acknowledgment.acknowledgmentHistory &&
                                        item.acknowledgment.acknowledgmentHistory.length > 0
                                    "
                                >
                                    <div class="history-note">
                                        <mat-icon class="inline-icon">history</mat-icon>
                                        Previously acknowledged
                                        {{ item.acknowledgment.acknowledgmentHistory.length }} time(s)
                                    </div>
                                </mat-card-content>

                                <mat-card-actions align="end">
                                    <button mat-button (click)="toggleExpand(item)" color="primary">
                                        <mat-icon>{{ item.isExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                                        {{ item.isExpanded ? 'Hide' : 'View' }} Policy
                                    </button>
                                </mat-card-actions>

                                <!-- Expandable Policy Content -->
                                <div
                                    *ngIf="item.isExpanded"
                                    class="policy-content"
                                    [innerHTML]="item.policy?.policyWriting || 'Loading...'"
                                ></div>
                            </mat-card>
                        </div>
                    </div>
                </mat-tab>
            </mat-tab-group>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding-right: 48px;
                position: relative;
            }

            .close-button {
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
            }

            .pending-badge {
                background: #ff9800;
                color: white;
                padding: 4px 12px;
                border-radius: 16px;
                font-size: 12px;
                font-weight: 500;
                margin-left: auto;
            }

            .dialog-content {
                min-height: 400px;
                padding: 0;
            }

            .loading-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 300px;
                gap: 16px;
            }

            .tab-icon {
                margin-right: 8px;
            }

            .tab-badge {
                background: #ff9800;
                color: white;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 11px;
                margin-left: 8px;
            }

            .tab-badge.success {
                background: #4caf50;
            }

            .tab-content {
                padding: 16px;
                max-height: 60vh;
                overflow-y: auto;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                text-align: center;
                color: #666;
            }

            .empty-icon {
                font-size: 64px;
                width: 64px;
                height: 64px;
                color: #4caf50;
                margin-bottom: 16px;
            }

            .policies-list {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            .policy-card {
                transition: box-shadow 0.2s ease;
            }

            .policy-card:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }

            .policy-avatar {
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
            }

            .pending-avatar {
                background: #fff3e0;
                color: #ff9800;
            }

            .acknowledged-avatar {
                background: #e8f5e9;
                color: #4caf50;
            }

            .reack-badge {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                background: color-mix(in srgb, var(--entity-color-policies, #00897b) 15%, transparent);
                color: var(--entity-color-policies, #00897b);
                padding: 2px 8px;
                border-radius: 4px;
                font-size: 12px;
            }

            .inline-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
            }

            .acknowledgment-progress {
                display: flex;
                gap: 24px;
                margin: 16px 0;
            }

            .step {
                display: flex;
                align-items: center;
                gap: 8px;
                color: #999;
            }

            .step.completed {
                color: #4caf50;
            }

            .step mat-icon {
                font-size: 20px;
                width: 20px;
                height: 20px;
            }

            .policy-content {
                padding: 16px;
                background: #f5f5f5;
                border-radius: 8px;
                margin-top: 16px;
                max-height: 300px;
                overflow-y: auto;
            }

            .history-note {
                display: flex;
                align-items: center;
                gap: 8px;
                color: #666;
                font-size: 13px;
                padding: 8px 0;
            }
        `,
    ],
})
export class OrgMyPoliciesDialogComponent implements OnInit, OnDestroy {
    public isLoading = true;
    public pendingPolicies: PolicyCard[] = [];
    public acknowledgedPolicies: PolicyCard[] = [];

    private subscription?: Subscription;
    private policyCache: Map<string, OrgPolicy> = new Map();

    constructor(
        private dialogRef: MatDialogRef<OrgMyPoliciesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: MyPoliciesDialogData,
        private db: AngularFirestore,
        private auth: AngularFireAuth,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.loadMyPolicies();
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private loadMyPolicies(): void {
        this.auth.user.pipe(take(1)).subscribe((user) => {
            if (!user || !this.data.organization?.key) {
                this.isLoading = false;
                return;
            }

            const orgKey = this.data.organization.key;
            const uid = user.uid;

            // Query acknowledgments for current user
            this.subscription = this.db
                .collection('organizations')
                .doc(orgKey)
                .collection('policyAcknowledgments', (ref) => ref.where('uid', '==', uid))
                .valueChanges()
                .subscribe(
                    (acknowledgments) => {
                        const pending: PolicyCard[] = [];
                        const acknowledged: PolicyCard[] = [];

                        (acknowledgments as PolicyAcknowledgment[]).forEach((ack) => {
                            const card: PolicyCard = {
                                acknowledgment: ack,
                                isExpanded: false,
                            };

                            if (ack.isComplete) {
                                acknowledged.push(card);
                            } else {
                                pending.push(card);
                            }
                        });

                        // Sort pending by requiresReacknowledgment first, then by createdAt
                        pending.sort((a, b) => {
                            if (a.acknowledgment.requiresReacknowledgment && !b.acknowledgment.requiresReacknowledgment)
                                return -1;
                            if (!a.acknowledgment.requiresReacknowledgment && b.acknowledgment.requiresReacknowledgment)
                                return 1;
                            return 0;
                        });

                        // Sort acknowledged by completedAt descending (most recent first)
                        acknowledged.sort((a, b) => {
                            const aTime =
                                (a.acknowledgment.completedAt as firebase.firestore.Timestamp)?.toMillis?.() || 0;
                            const bTime =
                                (b.acknowledgment.completedAt as firebase.firestore.Timestamp)?.toMillis?.() || 0;
                            return bTime - aTime;
                        });

                        this.pendingPolicies = pending;
                        this.acknowledgedPolicies = acknowledged;
                        this.isLoading = false;
                    },
                    (error) => {
                        console.error('Error loading acknowledgments:', error);
                        this.isLoading = false;
                    }
                );
        });
    }

    public async toggleExpand(item: PolicyCard): Promise<void> {
        item.isExpanded = !item.isExpanded;

        // Load policy content if not already loaded
        if (item.isExpanded && !item.policy) {
            await this.loadPolicyContent(item);
        }
    }

    private async loadPolicyContent(item: PolicyCard): Promise<void> {
        const policyKey = item.acknowledgment.policyKey;

        // Check cache first
        if (this.policyCache.has(policyKey)) {
            item.policy = this.policyCache.get(policyKey);
            return;
        }

        // Fetch from Firestore
        try {
            const policyDoc = await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(policyKey)
                .get()
                .toPromise();

            if (policyDoc?.exists) {
                const policy = policyDoc.data() as OrgPolicy;
                this.policyCache.set(policyKey, policy);
                item.policy = policy;
            }
        } catch (error) {
            console.error('Error loading policy:', error);
        }
    }

    public openAcknowledgmentDialog(item: PolicyCard): void {
        // Load policy content first if not already loaded
        if (!item.policy) {
            this.loadPolicyContent(item).then(() => {
                this.showAcknowledgmentDialog(item);
            });
        } else {
            this.showAcknowledgmentDialog(item);
        }
    }

    private showAcknowledgmentDialog(item: PolicyCard): void {
        const dialogData: PolicyAcknowledgmentDialogData = {
            organization: this.data.organization,
            acknowledgment: item.acknowledgment,
            policy: item.policy,
        };

        this.dialog
            .open(PolicyAcknowledgmentDialogComponent, {
                data: dialogData,
                width: '700px',
                maxHeight: '90vh',
                disableClose: false,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result?.completed) {
                    console.log('Policy acknowledged successfully:', result);
                    // The list will auto-update via the Firestore listener
                }
            });
    }

    public formatDate(timestamp: unknown): string {
        if (!timestamp) return '';

        try {
            const date = (timestamp as firebase.firestore.Timestamp).toDate();
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        } catch {
            return '';
        }
    }
}
